/**
 * AISearchPortal.com Landing Page
 * Combines invitation entry with chat.realestate design elements
 */

import React, { useEffect, useState, useRef } from 'react';
import { Container, Row, Col, Card, Button, Form, Alert } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHome,
  faLock,
  faUser,
  faEnvelope,
  faArrowRight,
  faCheckCircle,
  faShieldAlt,
  faBuilding,
  faComments,
  faRobot,
  faUserTie,
  faGavel
} from '@fortawesome/free-solid-svg-icons';
import axiosInstance from '../../helpers/api';
import portalConfig from '../../services/portal-config.service';
import './PortalLanding.css';

const PortalLanding: React.FC = () => {
  const navigate = useNavigate();
  const { token } = useParams<{ token?: string }>();
  const [inviteCode, setInviteCode] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [error, setError] = useState('');
  const [inviteData, setInviteData] = useState<any>(null);
  const [typingText, setTypingText] = useState('');
  const [promptIndex, setPromptIndex] = useState(0);

  // Dynamic prompts for typing effect
  const prompts = [
    "Find your perfect home with AI...",
    "Tell me about your dream home...",
    "3 bedrooms in Scottsdale under $800k...",
    "Family home near good schools...",
    "Modern condo with mountain views...",
  ];

  // Typing effect
  useEffect(() => {
    const currentPrompt = prompts[promptIndex];
    let charIndex = 0;
    let isDeleting = false;
    let typingSpeed = 100;
    
    const typeInterval = setInterval(() => {
      if (!isDeleting) {
        setTypingText(currentPrompt.slice(0, charIndex));
        charIndex++;
        
        if (charIndex > currentPrompt.length) {
          isDeleting = true;
          setTimeout(() => {}, 2000); // Pause at full text
        }
      } else {
        setTypingText(currentPrompt.slice(0, charIndex));
        charIndex--;
        typingSpeed = 50;
        
        if (charIndex === 0) {
          setPromptIndex((prev) => (prev + 1) % prompts.length);
          isDeleting = false;
        }
      }
    }, typingSpeed);
    
    return () => clearInterval(typeInterval);
  }, [promptIndex]);

  useEffect(() => {
    // If token in URL, validate immediately
    if (token) {
      validateInvite(token);
    }
  }, [token]);

  const validateInvite = async (code: string) => {
    setIsValidating(true);
    setError('');

    try {
      // First validate the invitation
      const response = await axiosInstance.get('/customer-portal/auth/validate-invite/' + code);

      if (response.data.success) {
        setInviteData(response.data.data);
        
        // Fetch buyer context with profile data
        try {
          const contextResponse = await axiosInstance.get('/customer-portal/buyer-context/' + code);
          
          if (contextResponse.data.success && contextResponse.data.buyerContext) {
            // Store buyer context for Theta AI
            const buyerContext = contextResponse.data.buyerContext;
            localStorage.setItem('portalBuyerContext', JSON.stringify(buyerContext));
            
            console.log('✅ Buyer profile loaded:', {
              hasProfile: !!buyerContext.searchCriteria,
              criteriaCount: Object.keys(buyerContext.searchCriteria || {}).length
            });
            
            // Store agent info for portal use
            if (buyerContext.agentInfo) {
              localStorage.setItem('portalAgentInfo', JSON.stringify(buyerContext.agentInfo));
            }
          }
        } catch (contextError) {
          console.warn('⚠️ Could not fetch buyer context:', contextError);
          // Continue anyway - portal will work without pre-populated data
        }
        
        // Refresh portal config with new token
        await portalConfig.refresh();
        
        // Redirect to agent's portal
        setTimeout(() => {
          navigate(`/${response.data.data.agentId || 'portal'}`, {
            state: { 
              inviteData: response.data.data,
              fromInvite: true 
            }
          });
        }, 1500);
      } else {
        setError('Invalid or expired invitation code.');
      }
    } catch (err) {
      setError('Unable to validate invitation. Please check your code and try again.');
    } finally {
      setIsValidating(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (inviteCode.trim()) {
      validateInvite(inviteCode.trim());
    }
  };

  // If already validated, show success state
  if (inviteData) {
    return (
      <div className="min-vh-100 d-flex align-items-center bg-light">
        <Container>
          <Row className="justify-content-center">
            <Col md={6}>
              <Card className="shadow-lg border-0">
                <Card.Body className="p-5 text-center">
                  <FontAwesomeIcon 
                    icon={faCheckCircle} 
                    size="4x" 
                    className="text-success mb-4" 
                  />
                  <h3 className="mb-3">Welcome{inviteData?.buyer?.firstName ? `, ${inviteData.buyer.firstName}` : ''}!</h3>
                  <p className="text-muted mb-4">
                    Your invitation has been verified. Connecting you with your agent...
                  </p>
                  
                  <div className="bg-light rounded p-3 mb-4">
                    <div className="d-flex align-items-center justify-content-center mb-2">
                      {inviteData?.agent?.photo ? (
                        <img 
                          src={inviteData.agent.photo} 
                          alt={inviteData?.agent?.name || 'Agent'}
                          className="rounded-circle me-3"
                          style={{ width: '50px', height: '50px', objectFit: 'cover' }}
                        />
                      ) : (
                        <div className="rounded-circle bg-primary d-flex align-items-center justify-content-center me-3"
                             style={{ width: '50px', height: '50px' }}>
                          <FontAwesomeIcon icon={faUser} className="text-white" />
                        </div>
                      )}
                      <div className="text-start">
                        <h6 className="mb-0">{inviteData?.agent?.name || 'Your Agent'}</h6>
                        <small className="text-muted">{inviteData?.brokerage?.name || 'Brokerage'}</small>
                      </div>
                    </div>
                  </div>
                  
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  return (
    <div className="portal-landing">
      {/* Header Navigation */}
      <header className="portal-header" style={{
        background: 'rgba(30, 41, 59, 0.85)',
        backdropFilter: 'blur(20px)',
        borderBottom: '1px solid rgba(129, 140, 248, 0.3)'
      }}>
        <Container>
          <nav className="navbar navbar-expand-lg navbar-dark py-3">
            <a className="navbar-brand d-flex align-items-center" href="/">
              <FontAwesomeIcon icon={faHome} className="me-2" size="lg" />
              <span className="fw-bold fs-4">AISearchPortal</span>
            </a>
            
            <button 
              className="navbar-toggler" 
              type="button" 
              data-bs-toggle="collapse" 
              data-bs-target="#navbarNav"
            >
              <span className="navbar-toggler-icon"></span>
            </button>
            
            <div className="collapse navbar-collapse" id="navbarNav">
              <ul className="navbar-nav ms-auto align-items-center">
                <li className="nav-item">
                  <a className="nav-link" href="#how-it-works">How It Works</a>
                </li>
                <li className="nav-item">
                  <a className="nav-link" href="/pricing">For Agents</a>
                </li>
                <li className="nav-item">
                  <a className="nav-link" href="#contact">Contact</a>
                </li>
                <li className="nav-item ms-lg-2">
                  <Button
                    variant="outline-light"
                    size="sm"
                    className="px-3"
                    onClick={() => navigate('/agent-portal/login')}
                  >
                    Agent Login
                  </Button>
                </li>
                <li className="nav-item ms-lg-2">
                  <Button
                    variant="warning"
                    size="sm"
                    className="px-3 fw-bold"
                    onClick={() => navigate('/pricing')}
                  >
                    Get Started
                  </Button>
                </li>
              </ul>
            </div>
          </nav>
        </Container>
      </header>
      
      {/* Hero Section with AI Background */}
      <section className="hero-section position-relative overflow-hidden" style={{
        background: 'linear-gradient(135deg, #1e293b 0%, #475569 25%, #6366f1 75%, #818cf8 100%)',
        minHeight: '100vh'
      }}>
        <div className="ai-background"></div>
        <Container className="position-relative z-1">
          <Row className="min-vh-100 align-items-center">
            <Col lg={6} className="text-white hero-content">
              <h1 className="display-3 fw-bold mb-4">
                Find Your Perfect Home with AI
              </h1>
              
              {/* Typing Effect Input Preview */}
              <div className="typing-preview mb-5 position-relative">
                <div className="fake-input d-flex align-items-center">
                  <FontAwesomeIcon icon={faComments} className="me-3 text-muted" />
                  <span className="typing-text">{typingText}<span className="cursor">|</span></span>
                </div>
              </div>

              <p className="lead mb-5">
                Your private AI-powered property search experience, 
                exclusively through your trusted real estate agent.
              </p>

              {/* How It Works Mini */}
              <div className="how-it-works-mini position-relative">
                <h5 className="mb-4">A smarter way to find your next home</h5>
                <Row className="g-4">
                  <Col xs={6}>
                    <div className="d-flex align-items-start">
                      <FontAwesomeIcon icon={faComments} className="me-3 mt-1" size="lg" />
                      <div>
                        <h6>Tell Us Your Needs</h6>
                        <small className="text-white-50">Natural language search</small>
                      </div>
                    </div>
                  </Col>
                  <Col xs={6}>
                    <div className="d-flex align-items-start">
                      <FontAwesomeIcon icon={faRobot} className="me-3 mt-1" size="lg" />
                      <div>
                        <h6>AI Finds Perfect Matches</h6>
                        <small className="text-white-50">Analyzes thousands of listings</small>
                      </div>
                    </div>
                  </Col>
                  <Col xs={6}>
                    <div className="d-flex align-items-start">
                      <FontAwesomeIcon icon={faUserTie} className="me-3 mt-1" size="lg" />
                      <div>
                        <h6>Connect with Your Agent</h6>
                        <small className="text-white-50">Direct expert guidance</small>
                      </div>
                    </div>
                  </Col>
                  <Col xs={6}>
                    <div className="d-flex align-items-start">
                      <FontAwesomeIcon icon={faHome} className="me-3 mt-1" size="lg" />
                      <div>
                        <h6>Find Your Home</h6>
                        <small className="text-white-50">Tour and close with confidence</small>
                      </div>
                    </div>
                  </Col>
                </Row>
              </div>
            </Col>

            <Col lg={6}>
              {/* Invitation Card */}
              <Card className="shadow-lg border-0 invitation-card ms-lg-5" id="invite-form">
                <Card.Body className="p-5">
                  <div className="text-center mb-4">
                    <FontAwesomeIcon icon={faShieldAlt} size="3x" className="text-primary mb-3" />
                    <h3 className="mb-2">Enter Your Invitation</h3>
                    <p className="text-muted">
                      Access your private AI-powered home search
                    </p>
                  </div>
                  
                  <Form onSubmit={handleSubmit}>
                    <Form.Group className="mb-4">
                      <Form.Label className="fw-semibold">Invitation Code</Form.Label>
                      <Form.Control
                        type="text"
                        size="lg"
                        placeholder="Enter your invitation code..."
                        value={inviteCode}
                        onChange={(e) => setInviteCode(e.target.value)}
                        disabled={isValidating}
                        className="text-center"
                        style={{ fontSize: '1.2rem', letterSpacing: '0.1em' }}
                      />
                      <Form.Text className="text-muted">
                        Your agent provided this code via email or text
                      </Form.Text>
                    </Form.Group>

                    {error && (
                      <Alert variant="danger" className="mb-4">
                        {error}
                      </Alert>
                    )}

                    <Button 
                      type="submit" 
                      variant="primary" 
                      size="lg" 
                      className="w-100 mb-4"
                      disabled={!inviteCode.trim() || isValidating}
                      style={{
                        background: 'linear-gradient(135deg, #6366f1 0%, #818cf8 100%)',
                        border: 'none',
                        padding: '14px 24px',
                        fontSize: '1.1rem',
                        fontWeight: '600',
                        boxShadow: '0 4px 14px rgba(99, 102, 241, 0.3)',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 8px 24px rgba(99, 102, 241, 0.4)';
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 4px 14px rgba(99, 102, 241, 0.3)';
                      }}
                    >
                      {isValidating ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" />
                          Validating...
                        </>
                      ) : (
                        <>
                          Access Portal
                          <FontAwesomeIcon icon={faArrowRight} className="ms-2" />
                        </>
                      )}
                    </Button>
                  </Form>

                  <hr className="my-4" />

                  {/* What is AISearchPortal */}
                  <div className="what-is-section">
                    <h5 className="mb-3">What is AISearchPortal?</h5>
                    
                    <div className="feature-list">
                      <div className="d-flex align-items-start mb-3">
                        <FontAwesomeIcon icon={faLock} className="text-primary me-3 mt-1" />
                        <div>
                          <strong>Private & Secure</strong>
                          <p className="text-muted small mb-0">
                            Invite-only access ensures privacy
                          </p>
                        </div>
                      </div>

                      <div className="d-flex align-items-start mb-3">
                        <FontAwesomeIcon icon={faHome} className="text-primary me-3 mt-1" />
                        <div>
                          <strong>AI-Powered Search</strong>
                          <p className="text-muted small mb-0">
                            Natural language property matching
                          </p>
                        </div>
                      </div>

                      <div className="d-flex align-items-start mb-3">
                        <FontAwesomeIcon icon={faUser} className="text-primary me-3 mt-1" />
                        <div>
                          <strong>Your Agent's Expertise</strong>
                          <p className="text-muted small mb-0">
                            Direct access to your trusted advisor
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="text-center mt-4">
                    <small className="text-muted">
                      Don't have an invitation? Ask your real estate agent
                    </small>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* AI Ethics Section */}
      <section className="ai-ethics-section py-5">
        <Container>
          <Row className="justify-content-center">
            <Col lg={10}>
              <div className="text-center mb-5">
                <h2 className="display-6 fw-bold mb-3">AI That Puts Ethics First</h2>
                <p className="lead text-muted">
                  Your trust is our foundation. Our AI technology is built with fairness, transparency, and compliance at its core.
                </p>
              </div>
              
              <Row className="g-4">
                <Col md={4}>
                  <Card className="h-100 border-0 shadow-sm ethics-card">
                    <Card.Body className="text-center p-4">
                      <div className="ethics-icon mb-3">
                        <FontAwesomeIcon icon={faShieldAlt} size="3x" className="text-primary" />
                      </div>
                      <h4 className="mb-3">Fair Housing Compliance</h4>
                      <p className="text-muted">
                        Fully compliant with all Fair Housing laws. Our AI never discriminates based on protected characteristics.
                      </p>
                    </Card.Body>
                  </Card>
                </Col>
                
                <Col md={4}>
                  <Card className="h-100 border-0 shadow-sm ethics-card">
                    <Card.Body className="text-center p-4">
                      <div className="ethics-icon mb-3">
                        <FontAwesomeIcon icon={faRobot} size="3x" className="text-primary" />
                      </div>
                      <h4 className="mb-3">Explainable AI</h4>
                      <p className="text-muted">
                        Transparent decision-making. Understand exactly why properties are recommended to you.
                      </p>
                    </Card.Body>
                  </Card>
                </Col>
                
                <Col md={4}>
                  <Card className="h-100 border-0 shadow-sm ethics-card">
                    <Card.Body className="text-center p-4">
                      <div className="ethics-icon mb-3">
                        <FontAwesomeIcon icon={faCheckCircle} size="3x" className="text-primary" />
                      </div>
                      <h4 className="mb-3">Zero Bias Search</h4>
                      <p className="text-muted">
                        Our algorithms focus solely on your stated preferences, ensuring unbiased property recommendations.
                      </p>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
              
              <div className="text-center mt-5">
                <p className="text-muted small">
                  <FontAwesomeIcon icon={faShieldAlt} className="me-2" />
                  Certified compliant with HUD Fair Housing guidelines and continuously audited for bias prevention.
                </p>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Comprehensive Footer */}
      <footer className="portal-footer">
        <Container>
          {/* Main Footer Content */}
          <Row className="footer-main py-5">
            {/* Company Info */}
            <Col md={4} className="mb-4">
              <div className="footer-brand">
                <h5 className="d-flex align-items-center mb-3">
                  <FontAwesomeIcon icon={faHome} className="me-2" />
                  AISearchPortal
                </h5>
                <p className="text-muted">
                  The invite-only AI-powered property search platform exclusively for real estate professionals and their clients.
                </p>
                <p className="text-muted small mt-3">
                  <strong>Powered by KairosIQ AI, Inc.</strong><br/>
                  Advanced AI technology for intelligent property matching and ethical real estate search.
                </p>
              </div>
            </Col>

            {/* Platform Links */}
            <Col md={2} className="mb-4">
              <h6 className="footer-title">Platform</h6>
              <ul className="footer-links">
                <li><a href="#how-it-works">How It Works</a></li>
                <li><a href="/for-agents">For Agents</a></li>
                <li><a href="/pricing">Pricing</a></li>
                <li><a href="/support">Support</a></li>
                <li><a href="/api-docs">API Documentation</a></li>
              </ul>
            </Col>

            {/* Legal & Compliance */}
            <Col md={3} className="mb-4">
              <h6 className="footer-title">Legal & Compliance</h6>
              <ul className="footer-links">
                <li>
                  <a href="/privacy-policy">
                    <FontAwesomeIcon icon={faShieldAlt} className="me-2" />
                    Privacy Policy
                  </a>
                </li>
                <li>
                  <a href="/terms-of-service">
                    <FontAwesomeIcon icon={faGavel} className="me-2" />
                    Terms of Service
                  </a>
                </li>
                <li>
                  <a href="/cookie-policy">
                    Cookie Policy
                  </a>
                </li>
                <li>
                  <a href="/data-processing">
                    Data Processing Agreement
                  </a>
                </li>
                <li>
                  <a href="/fair-housing">
                    Fair Housing Statement
                  </a>
                </li>
              </ul>
            </Col>

            {/* Equal Housing */}
            <Col md={3} className="mb-4">
              <h6 className="footer-title">Equal Housing Opportunity</h6>
              <div className="fair-housing-section text-center">
                {/* Fair Housing Logo SVG */}
                <svg 
                  className="fair-housing-logo mb-3" 
                  viewBox="0 0 100 100" 
                  xmlns="http://www.w3.org/2000/svg"
                  aria-label="Equal Housing Opportunity"
                  style={{ width: '80px', height: '80px' }}
                >
                  <path d="M50 15 L20 40 L20 70 L40 70 L40 50 L60 50 L60 70 L80 70 L80 40 Z" 
                        fill="#1a1a1a" 
                        stroke="#1a1a1a" 
                        strokeWidth="2"/>
                  <path d="M50 15 L80 40 M50 15 L20 40" 
                        fill="none" 
                        stroke="#1a1a1a" 
                        strokeWidth="3"/>
                  <rect x="85" y="35" width="12" height="3" fill="#1a1a1a"/>
                  <rect x="85" y="45" width="12" height="3" fill="#1a1a1a"/>
                  <text x="50" y="85" 
                        textAnchor="middle" 
                        fontSize="8" 
                        fill="#1a1a1a"
                        fontFamily="Arial, sans-serif"
                        fontWeight="bold">
                    EQUAL HOUSING
                  </text>
                  <text x="50" y="94" 
                        textAnchor="middle" 
                        fontSize="8" 
                        fill="#1a1a1a"
                        fontFamily="Arial, sans-serif"
                        fontWeight="bold">
                    OPPORTUNITY
                  </text>
                </svg>
                
                <p className="fair-housing-text text-muted small">
                  AISearchPortal is committed to Fair Housing and equal opportunity.
                </p>
              </div>
            </Col>
          </Row>

          {/* MLS & Technology Attribution */}
          <Row className="mls-attribution py-3 border-top">
            <Col>
              <p className="text-center text-muted small mb-0">
                Property data provided by participating real estate professionals and MLS organizations. 
                All information deemed reliable but not guaranteed. 
                AI search technology powered by KairosIQ AI, Inc. - Ethical AI for Real Estate.
              </p>
            </Col>
          </Row>

          {/* Bottom Bar */}
          <Row className="footer-bottom py-3 border-top">
            <Col md={6}>
              <p className="mb-0 text-muted">
                © {new Date().getFullYear()} AISearchPortal · All rights reserved
              </p>
            </Col>
            <Col md={6} className="text-md-end">
              <p className="mb-0 text-muted">
                <strong>Powered by</strong>{' '}
                <a href="https://kairosiq.ai" target="_blank" rel="noopener noreferrer" className="text-primary">
                  KairosIQ AI, Inc.
                </a>
              </p>
            </Col>
          </Row>
        </Container>
      </footer>
    </div>
  );
};

export default PortalLanding;